<template>
  <div
    :class="[
      'relative bg-cover bg-fixed bg-top bg-no-repeat bg-[#1D1D1D] pt-[100px]',
      backgroundClass,
    ]"
  >
  
    <!-- Content -->
    <div
      class="relative z-10 flex flex-col items-center text-white text-center py-12 px-4 sm:px-6"
    >
      <!-- Leaderboard Image Text -->
      <img :src="leaderboardImage" alt="Leaderboard Image" class="w-full max-w-[500px]" />

      <h1
        class="flex justify-center items-center gap-2 font-orbitron text-4xl sm:text-5xl lg:text-7xl font-black mt-12"
      >
        <!-- Image token (like chickengg / csgogem) -->
        <template v-if="leaderboardDisplay.type === 'image'">
          <img
            :src="leaderboardDisplay.src"
            alt="Token"
            class="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 object-contain"
          />
          {{ animatedPrize }}
        </template>

        <template v-else-if="leaderboardDisplay.type === 'suffix'">
          {{ animatedPrize }}<span>{{ leaderboardDisplay.value }}</span>
        </template>

        <template v-else>
          <span>{{ leaderboardDisplay.value }}</span
          >{{ animatedPrize }}
        </template>
      </h1>

      <h2 class="text-xl font-montserrat mt-2">PRIZE</h2>

      <!-- Leaderboard Switch Slider -->
      <div
        class="relative flex mt-10 bg-gradient-to-b from-[#1D1D1D] via-[#1D1D1D] to-[#313B44] rounded-[20px] p-2 max-w-4xl w-full justify-between overflow-hidden"
      >
        <!-- Sliding Background -->
        <div
          class="absolute top-2 bottom-2 w-[20%] rounded-[10px] transition-all duration-300 ease-in-out z-0 bg-gradient-to-r from-[#1294F0]/20 via-[#1294F0]/50 to-[#1294F0]/20"
          :class="{
            'left-2': currentLeaderboard === 'metaspins',
            'left-[20%]': currentLeaderboard === 'dailyspins',
            'left-[40%]': currentLeaderboard === 'raingg',
            'left-[60%]': currentLeaderboard === 'chickengg',
            'left-[80%]': currentLeaderboard === 'csgogem',
          }"
        ></div>

        <!-- Buttons -->
        <button
          v-for="site in ['metaspins', 'dailyspins', 'raingg', 'chickengg', 'csgogem']"
          :key="site"
          @click="setLeaderboard(site)"
          class="relative z-10 flex items-center justify-center w-1/5 py-2 rounded-[10px] transition-all duration-200 text-white"
          :class="currentLeaderboard !== site ? 'opacity-50 hover:opacity-80' : ''"
        >
          <img
            :src="getLeaderboardImage(site)"
            alt=""
            :class="[
              'h-auto transition-all duration-200',
              site === 'metaspins' ? ' w-[140px]' : '',
              site === 'dailyspins' ? 'w-[70px] sm:w-[120px] ' : '',
              site === 'raingg' ? 'w-[60px] sm:w-[100px]' : '',
              site === 'chickengg' || site === 'csgogem' ? 'w-[80px] sm:w-[120px]' : '',
            ]"
          />
        </button>
      </div>

      <!-- Leaderboard Table -->
      <div
        class="mt-10 bg-gradient-to-r from-[#1D1D1D] via-[#1D1D1D] to-[#313B44] p-4 sm:p-6 rounded-lg h-full w-full max-w-5xl"
      >
        <!-- Top 3 Leaderboard Cards -->
        <div class="flex justify-center gap-4 flex-wrap mt-10">
          <div
            v-for="(player, index) in top3"
            :key="index"
            :class="[
              'relative flex items-center gap-4 rounded-xl px-4 py-3 w-[300px]',
              index === 0
                ? 'bg-gradient-to-r from-[#A4790C]/20 via-[#A4790C]/50 to-[#A4790C]/20 border-2 border-[#B38B00]'
                : index === 1
                ? 'bg-gradient-to-r from-[#D0D0D0]/20 via-[#D0D0D0]/50 to-[#D0D0D0]/20 border-2 border-[#D0D0D0]'
                : 'bg-gradient-to-r from-[#DA3C01]/20 via-[#DA3C01]/50 to-[#DA3C01]/20 border-2 border-[#B65353]',
            ]"
          >
            <div class="absolute -top-5 left-[50px] -translate-x-1/2">
              <img
                :src="[
                  index === 0
                    ? require('@/assets/gold-crown.png')
                    : index === 1
                    ? require('@/assets/silver-crown.png')
                    : require('@/assets/bronze-crown.png'),
                ]"
                alt="Rank"
                class="w-12 h-12"
              />
            </div>
            <img
              v-if="player && !loading"
              :src="
                player.avatar
                  ? player.avatar
                  : require(`@/assets/avatar-${index + 1}.png`)
              "
              alt="avatar"
              class="w-16 h-16 rounded-full"
              :class="[
                index === 0
                  ? 'bg-[#FFE29B]'
                  : index === 1
                  ? 'bg-[#CFF1D3]'
                  : 'bg-[#C7EBFF]',
              ]"
            />
            <div
              v-else
              :class="[
                'w-16 h-16 rounded-full animate-pulse',
                index === 0
                  ? 'bg-[#FFE29B]'
                  : index === 1
                  ? 'bg-[#CFF1D3]'
                  : 'bg-[#C7EBFF]',
              ]"
            ></div>

            <div class="flex flex-col text-white">
              <h3 class="font-bold text-[16px]">
                {{ loading ? "" : getMaskedUsername(player?.username) }}
              </h3>

              <!-- Total Wagered or XP -->
              <p
                class="text-[13px]"
                :class="index === 0 ? 'text-yellow-200' : 'text-[#D3D3D3]'"
              >
                <template v-if="currentLeaderboard === 'chickengg'">
                  XP:
                  <img
                    src="@/assets/xp-token.png"
                    alt="XP Token"
                    class="inline w-4 h-4 ml-1 mr-1"
                  />
                </template>

                <template v-else-if="currentLeaderboard === 'csgogem'">
                  Total Points:
                </template>

                <template v-else> Total Wagered: </template>

                <span class="text-[#F9B841] font-semibold inline-flex items-center gap-1">
                  <template v-if="loading"></template>

                  <template v-else-if="currentLeaderboard === 'chickengg'">
                    <template
                      v-if="typeof getCurrencyFormatted(player?.wagered) === 'object'"
                    >
                      {{ getCurrencyFormatted(player?.wagered).value }}
                      <img
                        src="@/assets/chicken-token.avif"
                        alt="Token"
                        class="w-4 h-4"
                      />
                    </template>
                    <template v-else>
                      {{ getCurrencyFormatted(player?.wagered) }}
                    </template>
                  </template>

                  <template v-else>
                    {{ getCurrencyFormatted(player?.wagered) }}
                  </template>
                </span>
              </p>

              <!-- Prize -->
              <p
                class="text-[13px]"
                :class="index === 0 ? 'text-yellow-200' : 'text-[#D3D3D3]'"
              >
                Prize:
                <span class="text-[#EEAF0E] font-semibold inline-flex items-center gap-1">
                  <template v-if="loading"></template>
                  <template v-else-if="currentLeaderboard === 'chickengg'">
                    <img src="@/assets/chicken-token.avif" alt="Token" class="w-4 h-4" />
                    {{ getRewardValue(index + 1) }}
                  </template>
                  <template v-else-if="currentLeaderboard === 'csgogem'">
                    <img src="@/assets/gem-token.avif" alt="Gem Token" class="w-4 h-4" />
                    {{ getRewardValue(index + 1) }}
                  </template>
                  <template v-else>
                    {{ getRewardValue(index + 1) }}
                  </template>
                </span>
              </p>

              <!-- Bonus Buy / Bonus -->
              <!-- <p
                v-if="['metaspins'].includes(currentLeaderboard)"
                :class="[
                  'text-[13px]',
                  index === 0 ? 'text-yellow-200' : 'text-[#D3D3D3]',
                ]"
              >
              Bonus Buy:
                <span class="text-[#EEAF0E] font-semibold">
                  {{ loading ? "" : getBonusValue(index) }}
                </span>
              </p> -->
            </div>
          </div>
        </div>

        <!-- Countdown Timer -->
        <div
          class="mt-10 flex flex-col justify-center items-center text-center text-white"
        >
          <p class="text-sm font-medium mb-4">
            {{ currentLeaderboardLabel }} Leaderboard ends in
          </p>

          <!-- Countdown values with loading state -->
          <div v-if="loading" class="flex justify-center gap-2 sm:gap-4 text-black">
            <div
              class="bg-[#1D1D1D] border border-[#363636] rounded-md px-4 py-4 text-white flex justify-center items-center"
            >
              <span
                class="animate-spin border-2 border-white border-t-transparent rounded-full w-7 sm:w-8 h-7 sm:h-8"
              ></span>
            </div>
            <div
              class="bg-[#1D1D1D] border border-[#363636] rounded-md px-4 py-4 text-white flex justify-center items-center"
            >
              <span
                class="animate-spin border-2 border-white border-t-transparent rounded-full w-7 sm:w-8 h-7 sm:h-8"
              ></span>
            </div>
            <div
              class="bg-[#1D1D1D] border border-[#363636] rounded-md px-4 py-4 text-white flex justify-center items-center"
            >
              <span
                class="animate-spin border-2 border-white border-t-transparent rounded-full w-7 sm:w-8 h-7 sm:h-8"
              ></span>
            </div>
            <div
              class="bg-[#1D1D1D] border border-[#363636] rounded-md px-4 py-4 text-white flex justify-center items-center"
            >
              <span
                class="animate-spin border-2 border-white border-t-transparent rounded-full w-7 sm:w-8 h-7 sm:h-8"
              ></span>
            </div>
          </div>

          <!-- Countdown values when not loading -->
          <div v-else class="flex justify-center gap-2 sm:gap-4 text-black">
            <div
              class="bg-[#1D1D1D] border border-[#363636] rounded-md px-4 py-4 text-white"
            >
              <p class="text-base sm:text-xl font-bold">{{ days }}d</p>
            </div>
            <div
              class="bg-[#1D1D1D] border border-[#363636] rounded-md px-4 py-4 text-white"
            >
              <p class="text-base sm:text-xl font-bold">{{ hours }}h</p>
            </div>
            <div
              class="bg-[#1D1D1D] border border-[#363636] rounded-md px-4 py-4 text-white"
            >
              <p class="text-base sm:text-xl font-bold">{{ minutes }}m</p>
            </div>
            <div
              class="bg-[#1D1D1D] border border-[#363636] rounded-md px-4 py-4 text-white"
            >
              <p class="text-base sm:text-xl font-bold">{{ seconds }}s</p>
            </div>
          </div>

          <div
            class="w-full lg:w-[700px] mt-6 text-xs sm:text-sm text-gray-300 border border-[#363636] rounded-md p-3"
          >
            All Wagers are counted during the inclusive dates:
            <span class="text-[#1EA7FD] font-semibold block mt-4 sm:inline sm:mt-0">
              {{ startDate }} - {{ endDate }}
            </span>
          </div>
        </div>

        <!-- Current / Previous Toggle -->
        <div class="flex justify-center gap-4 my-6">
          <!-- Current Button -->
          <button
            @click="
              previousLeaderboard = false;
              changeLeaderboard();
            "
            :class="[
              'w-36 py-2 rounded-xl text-base font-semibold border transition-all duration-200 shadow-sm',
              previousLeaderboard
                ? 'bg-[#1e1e1e] border-[#363636] text-gray-400 hover:text-white hover:border-gray-500'
                : 'bg-gradient-to-r from-yellow-400 to-yellow-300 text-black border-yellow-400 shadow-md',
            ]"
          >
            Current
          </button>

          <!-- Previous Button -->
          <button
            @click="
              previousLeaderboard = true;
              changeLeaderboard();
            "
            :class="[
              'w-36 py-2 rounded-xl text-base font-semibold border transition-all duration-200 shadow-sm',
              previousLeaderboard
                ? 'bg-gradient-to-r from-yellow-400 to-yellow-300 text-black border-yellow-400 shadow-md'
                : 'bg-[#1e1e1e] border-[#363636] text-gray-400 hover:text-white hover:border-gray-500',
            ]"
          >
            Previous
          </button>
        </div>

        <!-- Avatar and name loading state -->
        <div v-if="loading" class="flex justify-center items-center min-h-[300px]">
          <span
            class="animate-spin border-2 border-white border-t-transparent rounded-full w-12 h-12"
          ></span>
        </div>

        <!-- Scrollable only on mobile -->
        <div class="mt-8 overflow-x-auto sm:overflow-visible">
          <div class="space-y-2 min-w-[640px] sm:min-w-0">
            <div
              v-for="(user, index) in topRest"
              :key="index"
              class="flex items-center justify-between rounded-md py-4 px-5"
              :class="index % 2 === 0 ? 'bg-[#1294F0]/10' : 'bg-transparent'"
            >
              <!-- Rank + Avatar + Name -->
              <div class="flex items-center gap-3 w-[45%] min-w-[200px]">
                <span class="text-white font-bold w-5 text-right">{{ index + 4 }}</span>
                <img
                  :src="user.avatar ? user.avatar : require('@/assets/avatar-1.png')"
                  alt="avatar"
                  class="w-10 h-10 rounded-full"
                />
                <span class="text-white truncate font-golos font-bold">
                  {{ user ? getMaskedUsername(user.username) : "" }}
                </span>
              </div>

              <!-- Wagered / XP / Points -->
              <div class="text-right w-[25%] min-w-[100px]">
                <!-- Header Label -->
                <div
                  class="text-sm text-white/60 font-montserrat flex justify-end items-center gap-1"
                >
                  <template v-if="currentLeaderboard === 'chickengg'"> XP </template>
                  <template v-else-if="currentLeaderboard === 'csgogem'">
                    Points
                  </template>
                  <template v-else> Wagered </template>
                </div>

                <!-- Loading Spinner -->
                <div v-if="!user" class="text-white font-bold text-base font-orbitron">
                  <span
                    class="animate-spin border-2 border-white border-t-transparent rounded-full w-12 h-12"
                  ></span>
                </div>

                <!-- Value Display -->
                <div
                  v-else
                  class="text-white font-bold text-xs md:text-base font-orbitron flex justify-end items-center gap-1"
                >
                  <template v-if="currentLeaderboard === 'chickengg'">
                    <img src="@/assets/xp-token.png" alt="XP Token" class="w-5 h-5" />
                    {{ getCurrencyFormatted(user.wagered) }}
                  </template>
                  <template v-else>
                    {{ getCurrencyFormatted(user.wagered) }}
                  </template>
                </div>
              </div>

              <!-- Prize -->
              <div class="text-right w-[20%] min-w-[100px]">
                <div class="text-sm text-white/60 font-montserrat">Prize</div>
                <div
                  v-if="!user"
                  class="text-[#F9B841] font-bold text-base font-orbitron"
                >
                  <span
                    class="animate-spin border-2 border-white border-t-transparent rounded-full w-12 h-12"
                  ></span>
                </div>
                <div
                  v-else
                  class="text-[#F9B841] font-bold text-xs md:text-base font-orbitron flex justify-end items-center gap-1"
                >
                  <img
                    v-if="currentLeaderboard === 'chickengg'"
                    src="@/assets/chicken-token.avif"
                    alt="Chicken Token"
                    class="w-4 h-4"
                  />
                  <img
                    v-if="currentLeaderboard === 'csgogem'"
                    src="@/assets/gem-token.avif"
                    alt="Gem Token"
                    class="w-4 h-4"
                  />
                  <span>{{ getRewardValue(index + 4) }}</span>
                </div>
              </div>

              <!-- Reward Column (Bonus Buy / Free Spins / Bonus) -->
              <!-- <div
                v-if="
                  currentLeaderboard === 'metaspins'
                "
                class="text-right w-[20%] min-w-[120px]"
              >
                <div class="text-sm text-white/60 font-montserrat">
                  {{
                    currentLeaderboard === "metaspins"
                      ? index + 3 >= 14
                        ? "Free Spins"
                        : "Bonus Buy"
                      : ""
                  }}
                </div>
                <div
                  v-if="!user"
                  class="text-[#F9B841] font-bold text-base font-orbitron"
                >
                  <span
                    class="animate-spin border-2 border-white border-t-transparent rounded-full w-12 h-12"
                  ></span>
                </div>
                <div
                  v-if="currentLeaderboard === 'metaspins'"
                  class="text-[#F9B841] font-bold text-xs md:text-base font-orbitron"
                >
                  {{ getBonusValue(index + 3) || "--" }}
                </div>
              </div> -->
            </div>
          </div>
        </div>
      </div>

      <!-- Disclaimer Dropdown -->
      <!-- <div class="w-full max-w-6xl mt-10">
        <div
        
          class="bg-gradient-to-r from-[#1D1D1D]/50 to-[#313B44]/50 rounded-lg p-4 flex items-center justify-between cursor-pointer select-none"
          @click.prevent="toggleDisclaimer"
        >
          <div class="flex items-center gap-2">
            <img
              src="@/assets/question-mark.png"
              alt="Lightbulb"
              class="w-10 h-10 sm:w-10 sm:h-10 object-contain"
            />
            <span class="text-white font-bold text-base sm:text-lg font-montserrat"
              >DISCLAIMER</span
            >
          </div>
          <span
            class="text-white font-bold text-lg sm:text-xl transition-transform duration-300"
            :class="{ 'rotate-180': showDisclaimer }"
          >
            ▼
          </span>
        </div>

        <transition name="fade">
          <div
            v-if="showDisclaimer"
            class="bg-gradient-to-r from-[#1D1D1D]/50 to-[#313B44]/50 text-white p-3 sm:p-4 rounded-b-lg mt-1 text-sm sm:text-base"
          >
            Your wagers on <span class="font-bold text-[#FFD703]">Roobet</span> will count
            towards the leaderboard at the following weights based on the games you are
            playing. This helps prevent leaderboard abuse: <br /><br />
            *
            <span class="font-bold text-[#FFD703]">Games with an RTP under 96%</span>
            will contribute <span class="font-bold text-[#FFD703]">100%</span> of the
            amount wagered to the leaderboard. <br /><br />
            *
            <span class="font-bold text-[#FFD703]">Games with an RTP of 97%</span> will
            contribute <span class="font-bold text-[#FFD703]">50%</span> of the amount
            wagered to the leaderboard. <br /><br />
            *
            <span class="font-bold text-[#FFD703]"
              >Games with an RTP of 98-99% and above</span
            >
            will contribute <span class="font-bold text-[#FFD703]">10%</span> of the
            amount wagered to the leaderboard. <br /><br />
            *
            <span class="font-bold text-[#FFD703]">Only house games and slots count</span>
            towards the leaderboard. <br /><br />
            Only Slots and Housegames count
            <span class="font-black text-[#FFD703]">(Dice excluded).</span>
          </div>
        </transition>
      </div> -->

      <div
        class="w-full max-w-6xl mt-10"
        v-if="
          currentLeaderboard !== 'empiredrop' &&
          currentLeaderboard !== 'raingg' &&
          currentLeaderboard !== 'chickengg' &&
          currentLeaderboard !== 'csgogem'
        "
      >
        <div
          class="bg-gradient-to-r from-[#1D1D1D]/50 to-[#313B44]/50 rounded-lg p-4 flex items-center justify-between cursor-pointer select-none"
          @click.prevent="toggleDisclaimer"
        >
          <div class="flex items-center gap-2">
            <img
              src="@/assets/question-mark.png"
              alt="Lightbulb"
              class="w-10 h-10 sm:w-10 sm:h-10 object-contain"
            />
            <span class="text-white font-bold text-base sm:text-lg font-montserrat"
              >RULES</span
            >
          </div>
          <span
            class="text-white font-bold text-lg sm:text-xl transition-transform duration-300"
            :class="{ 'rotate-180': showDisclaimer }"
          >
            ▼
          </span>
        </div>

        <transition name="fade">
          <div
            v-if="showDisclaimer && currentLeaderboard === 'metaspins'"
            class="bg-gradient-to-r from-[#1D1D1D]/50 to-[#313B44]/50 text-white p-3 sm:p-4 rounded-b-lg mt-1 text-sm sm:text-base"
          >
            *
            <span class="font-bold text-[#FFD703]">Only Slots count</span>
            as <span class="font-bold text-[#FFD703]">100%</span> towards the leaderboard.
            <br /><br />

            * <span class="font-bold text-[#FFD703]">House games</span> such as Baccarat,
            Plinko, etc. <span class="font-bold text-[#FFD703]">DO NOT</span> count
            towards the leaderboard. <br /><br />

            * A <span class="font-bold text-[#FFD703]">minimum of $5,000 wagered</span> is
            required to claim leaderboard rewards.
          </div>
        </transition>

        <transition name="fade">
          <div
            v-if="showDisclaimer && currentLeaderboard === 'dailyspins'"
            class="bg-gradient-to-r from-[#1D1D1D]/50 to-[#313B44]/50 text-white p-3 sm:p-4 rounded-b-lg mt-1 text-sm sm:text-base"
          >
            *
            <span class="font-bold text-[#FFD703]">Only Slots count</span>
            as <span class="font-bold text-[#FFD703]">100%</span> towards the leaderboard.
            <br /><br />

            * <span class="font-bold text-[#FFD703]">House games</span> such as Baccarat,
            Plinko, etc. <span class="font-bold text-[#FFD703]">DO NOT</span> count
            towards the leaderboard. <br /><br />

            * A <span class="font-bold text-[#FFD703]">minimum of $2,000 wagered</span> is
            required to claim leaderboard rewards. <br /><br />

            * Prizes will be <span class="font-bold text-[#FFD703]">IN AUD</span>
          </div>
        </transition>
      </div>

      <!-- Title -->
      <!-- <div
        class="w-full max-w-6xl flex justify-center lg:justify-start items-center mt-10"
      >
        <h1
          class="font-montserrat font-bold text-white text-2xl lg:text-3xl ml-4 sm:ml-8"
        >
          How it Works
        </h1>
      </div> -->

      <!-- Cards -->
      <div
        class="grid w-full max-w-6xl grid-cols-1 gap-6 mt-8 sm:grid-cols-2 lg:grid-cols-3"
      >
        <div class="flex justify-center items-center">
          <img
            src="@/assets/leaderboard-card1.png"
            alt="Referral"
            class="w-full max-w-full object-contain"
          />
        </div>
        <div class="flex justify-center items-center">
          <img
            src="@/assets/leaderboard-card2.png"
            alt="Profile Settings"
            class="w-full max-w-full object-contain"
          />
        </div>
        <div class="flex justify-center items-center">
          <a href="https://discord.gg/ballyboy" target="_blank" rel="noopener noreferrer">
            <img
              src="@/assets/leaderboard-card3.png"
              alt="Monthly Reset"
              class="w-full max-w-full object-contain cursor-pointer"
            />
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment-timezone";
import { useRoute, useRouter } from "vue-router";

export default {
  name: "LeaderboardPage",
  data() {
    const route = useRoute();
    const validTypes = ["metaspins", "dailyspins", "raingg", "chickengg", "csgogem"];
    return {
      currentLeaderboard: validTypes.includes(route.query.type)
        ? route.query.type
        : "metaspins",
      route,
      router: useRouter(),
      leaderboardData: {},
      leaderboardPrevData: {},
      top3: [],
      topRest: [],
      animatedPrize: "0",
      totalPrize: 0,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      previousLeaderboard: false,
      startDate: null,
      endDate: null,
      notFormattedEndDate: null,
      loading: false,
      timerInterval: null,
      showDisclaimer: false,
    };
  },
  watch: {
    "route.query.type"(newType) {
      if (
        ["metaspins", "dailyspins", "raingg", "chickengg", "csgogem"].includes(newType)
      ) {
        this.currentLeaderboard = newType;
      }
    },
    currentLeaderboard() {
      this.resetLeaderboardState();

      this.fetchLeaderboard();
      this.startTimer();
    },
  },
  computed: {
    leaderboardDisplay() {
      switch (this.currentLeaderboard) {
        case "empiredrop":
          return { type: "symbol", value: "€" };
        case "raingg":
          return { type: "suffix", value: "c" };
        case "chickengg":
          return { type: "image", src: require("@/assets/chicken-token.avif") };
        case "csgogem":
          return { type: "image", src: require("@/assets/gem-token.avif") };
        default:
          return { type: "symbol", value: "$" };
      }
    },
    currentLeaderboardLabel() {
      const labels = {
        dailyspins: "Dailyspins",
        metaspins: "Metaspins",
        // empiredrop: "Empire Drop",
        raingg: "Rain.GG",
        chickengg: "Chicken.GG",
        csgogem: "Csgogem",
      };
      return labels[this.currentLeaderboard] || "";
    },
    backgroundClass() {
      switch (this.currentLeaderboard) {
        case "dailyspins":
          return "bg-dailyspins-bg";
        case "metaspins":
          return "bg-metaspins-bg";
        // case "empiredrop":
        //   return "bg-empiredrop-bg";
        case "raingg":
          return "bg-raingg-bg";
        case "chickengg":
          return "bg-chickengg-bg";
        case "csgogem":
          return "bg-csgogem-bg";
        default:
          return "";
      }
    },
    leaderboardImage() {
      switch (this.currentLeaderboard) {
        case "dailyspins":
          return require("@/assets/dailyspins-leaderboard-text.png");
        case "metaspins":
          return require("@/assets/metaspins-leaderboard-text.png");
        // case "empiredrop":
        //   return require("@/assets/empiredrop-leaderboard-text.png");
        case "raingg":
          return require("@/assets/raingg-leaderboard-text.png");
        case "chickengg":
          return require("@/assets/chickengg-leaderboard-text.png");
        case "csgogem":
          return require("@/assets/csgogem-leaderboard-text.png");
        default:
          return "";
      }
    },
    leaderboardSwitchImages() {
      return {
        dailyspins: {
          active: require("@/assets/dailyspins-active.png"),
          inactive: require("@/assets/dailyspins-inactive.png"),
        },
        metaspins: {
          active: require("@/assets/metaspins-active.png"),
          inactive: require("@/assets/metaspins-inactive.png"),
        },
        // empiredrop: {
        //   active: require("@/assets/empiredrop-active.png"),
        //   inactive: require("@/assets/empiredrop-inactive.png"),
        // },
        raingg: {
          active: require("@/assets/raingg-active.png"),
          inactive: require("@/assets/raingg-inactive.png"),
        },
        chickengg: {
          active: require("@/assets/chickengg-active.png"),
          inactive: require("@/assets/chickengg-inactive.png"),
        },
        csgogem: {
          active: require("@/assets/csgogem-active.png"),
          inactive: require("@/assets/csgogem-inactive.png"),
        },
      };
    },
  },
  methods: {
    async fetchLeaderboard() {
      this.loading = true;

      const baseUrl = process.env.VUE_APP_BASE_API;
      const apiKey = process.env.VUE_APP_X_API_KEY;

      try {
        const currentRes = await axios.get(
          `${baseUrl}/api/leaderboard/${
            this.currentLeaderboard === "empiredrop"
              ? "empire-drop"
              : this.currentLeaderboard
          }`,
          {
            headers: {
              "x-api-key": apiKey,
            },
          }
        );

        this.leaderboardData = currentRes.data || {};
        this.startDate = moment(this.leaderboardData.startDate).format("MMMM DD, YYYY");
        this.endDate = moment(this.leaderboardData.endDate).format("MMMM DD, YYYY");
        this.notFormattedEndDate = this.leaderboardData.endDate;

        if (this.shouldFetchPreviousLeaderboard()) {
          const previousRes = await axios.get(
            `${baseUrl}/api/leaderboard/${
              this.currentLeaderboard === "empiredrop"
                ? "empire-drop"
                : this.currentLeaderboard
            }?previous=true`,
            {
              headers: {
                "x-api-key": apiKey,
              },
            }
          );

          this.leaderboardPrevData = previousRes.data || {};
        }

        this.leaderboardData = this.normalizeLeaderboardData(this.leaderboardData);
        this.leaderboardPrevData = this.normalizeLeaderboardData(
          this.leaderboardPrevData
        );

        this.updateTotalPrize();

        this.changeLeaderboard();
        this.startTimer();
      } catch (error) {
        console.error("Failed to fetch leaderboard data:", error);
      } finally {
        this.loading = false;
      }
    },

    shouldFetchPreviousLeaderboard() {
      if (this.currentLeaderboard === "dailyspins") {
        return process.env.VUE_APP_SHOW_PREVIOUS_LEADERBOARD_DAILYSPINS === "true";
      } else if (this.currentLeaderboard === "metaspins") {
        return process.env.VUE_APP_SHOW_PREVIOUS_LEADERBOARD_METASPINS === "true";
      } else if (this.currentLeaderboard === "empiredrop") {
        return process.env.VUE_APP_SHOW_PREVIOUS_LEADERBOARD_EMPIREDROP === "true";
      } else if (this.currentLeaderboard === "raingg") {
        return process.env.VUE_APP_SHOW_PREVIOUS_LEADERBOARD_RAINGG === "true";
      } else if (this.currentLeaderboard === "chickengg") {
        return process.env.VUE_APP_SHOW_PREVIOUS_LEADERBOARD_CHICKENGG === "true";
      }
      return false;
    },

    normalizeLeaderboardData(data) {
      if (!data?.leaderboard) return { leaderboard: [] };

      return {
        startDate: data.startDate,
        endDate: data.endDate,
        leaderboard: data.leaderboard.map((entry) => ({
          username: this.getUsername(entry),
          wagered: this.getWageredAmount(entry),
          avatar: this.getAvatar(entry),
          discordId: entry.userDetails?.discordId || null,
        })),
      };
    },

    getUsername(entry) {
      if (this.currentLeaderboard === "chickengg") {
        return entry.displayName || "";
      }

      if (
        ["dailyspins", "metaspins", "raingg", "csgogem"].includes(this.currentLeaderboard)
      ) {
        return entry.username || "";
      } else if (this.currentLeaderboard === "empiredrop") {
        return entry.user?.name || "";
      }

      return "";
    },

    getWageredAmount(entry) {
      if (this.currentLeaderboard === "empiredrop") {
        const wagered = entry.total_value
          ? parseFloat(entry.total_value.replace(/,/g, ""))
          : 0;
        return wagered || 0;
      }

      if (this.currentLeaderboard === "raingg") {
        const wagered = entry.wagered ? parseFloat(entry.wagered) : 0;
        return wagered || 0;
      }

      if (["metaspins", "dailyspins"].includes(this.currentLeaderboard)) {
        return Number(entry.bets || 0);
      }

      if (this.currentLeaderboard === "chickengg") {
        return Number(entry.xpEarned || 0);
      }

      if (this.currentLeaderboard === "csgogem") {
        return Number(entry.wagered || 0);
      }

      return 0;
    },

    getAvatar(entry) {
      if (entry?.userDetails?.discordId && entry?.userDetails?.avatar) {
        const avatarUrl = `https://cdn.discordapp.com/avatars/${
          entry.userDetails.discordId
        }/${entry.userDetails.avatar}.png?timestamp=${Date.now()}`;
        console.log("Generated Discord Avatar URL:", avatarUrl);
        return avatarUrl;
      }

      return require("@/assets/avatar-1.png");
    },

    getBonusValue(index) {
      const type = this.currentLeaderboard.toUpperCase();
      const isPrevious = this.previousLeaderboard;
      const envKey = `VUE_APP_${type}_${
        isPrevious ? "PREVIOUS" : "CURRENT"
      }_BONUS_DISTRIBUTION`;
      const bonusArr = JSON.parse(process.env[envKey] || "[]");

      if (bonusArr?.[index] == null) {
        return "--";
      }

      const value = bonusArr[index];

      if (this.currentLeaderboard === "chickengg") {
        return `${value} coins battle`;
      }

      if (this.currentLeaderboard === "metaspins" && index >= 14 && index <= 19) {
        return value;
      }

      return `$${value}`;
    },

    toggleLeaderboard(leaderboardType) {
      if (this.currentLeaderboard !== leaderboardType) {
        this.currentLeaderboard = leaderboardType;

        this.changeLeaderboard();
      }
    },

    changeLeaderboard() {
      const data = this.previousLeaderboard
        ? this.leaderboardPrevData.leaderboard
        : this.leaderboardData.leaderboard;

      // console.log("Leaderboard Data:", data);

      this.top3 = data?.slice(0, 3) || [];

      if (this.currentLeaderboard === "chickengg") {
        this.top3 = data?.slice(0, 3) || [];
        this.topRest = data?.slice(3, 8) || [];
      } else if (this.currentLeaderboard === "metaspins") {
        this.top3 = data?.slice(0, 3) || [];
        this.topRest = data?.slice(3, 20) || [];
      } else {
        this.top3 = data?.slice(0, 3) || [];
        this.topRest = data?.slice(3, 10) || [];
      }

      this.updateTotalPrize();

      this.startDate = moment(
        this.previousLeaderboard
          ? this.leaderboardPrevData.startDate
          : this.leaderboardData.startDate
      ).format("MMMM DD, YYYY");

      this.endDate = moment(
        this.previousLeaderboard
          ? this.leaderboardPrevData.endDate
          : this.leaderboardData.endDate
      ).format("MMMM DD, YYYY");

      this.notFormattedEndDate = this.previousLeaderboard
        ? this.leaderboardPrevData.endDate
        : this.leaderboardData.endDate;

      this.startTimer();
    },

    getRewardValue(rank) {
      const isPrevious = this.previousLeaderboard;
      const rewardsKey = `VUE_APP_${this.currentLeaderboard.toUpperCase()}_LEADERBOARD_${
        isPrevious ? "PREV" : "CURRENT"
      }_PRIZES`;

      const prizes = JSON.parse(process.env[rewardsKey] || "[]");
      const adjustedRank = rank - 1;

      if (adjustedRank >= prizes.length) return "-";

      const reward = prizes[adjustedRank];

      // ChickenGG special handling
      if (this.currentLeaderboard === "chickengg") {
        return typeof reward === "object" && reward !== null ? reward.value : reward;
      }

      // Other leaderboards with currency formatting
      switch (this.currentLeaderboard) {
        case "empiredrop":
          return typeof reward === "number" ? `€${reward}` : reward;
        case "raingg":
          return typeof reward === "number" ? `${reward}c` : reward;
        case "dailyspins":
        case "metaspins":
          return typeof reward === "number" ? `$${reward}` : reward;
        default:
          return reward;
      }
    },
    startTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }

      const getNextTargetDate = () => {
        return moment.utc(this.notFormattedEndDate).subtract(1, "seconds");
      };

      const targetDate = getNextTargetDate();

      const updateTimer = () => {
        const nowUtc = moment.utc();
        const distance = targetDate.diff(nowUtc);

        if (distance < 0) {
          clearInterval(this.timerInterval);
          this.timerInterval = null;

          this.days = 0;
          this.hours = 0;
          this.minutes = 0;
          this.seconds = 0;
          this.timer = "0d 0h 0m 0s";
        } else {
          const duration = moment.duration(distance);
          this.days = Math.floor(duration.asDays());
          this.hours = duration.hours();
          this.minutes = duration.minutes();
          this.seconds = duration.seconds();

          this.timer = `${this.days}d ${this.hours}h ${this.minutes}m ${this.seconds}s`;
        }
      };

      updateTimer();
      this.timerInterval = setInterval(updateTimer, 1000);
    },

    getMaskedUsername(username) {
      if (!username || typeof username !== "string") {
        return "";
      }

      if (username.length <= 4) {
        return username;
      }

      const masked = "*".repeat(4);
      const rest = username.slice(4);
      return `${masked}${rest}`;
    },

    getCurrencyFormatted(value) {
      const num = Number(value);

      if (isNaN(num)) return "";

      const formatted = num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      if (this.currentLeaderboard === "dailyspins") {
        return `$${formatted}`;
      } else if (this.currentLeaderboard === "metaspins") {
        return `$${formatted}`;
      } else if (this.currentLeaderboard === "empiredrop") {
        return `€${formatted}`;
      } else if (this.currentLeaderboard === "raingg") {
        return `${formatted}c`;
      } else if (this.currentLeaderboard === "chickengg") {
        return `${formatted}`;
      }

      return formatted;
    },

    getOrdinalSuffix(n) {
      const s = ["th", "st", "nd", "rd"],
        v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    },

    setLeaderboard(site) {
      if (this.currentLeaderboard !== site) {
        this.currentLeaderboard = site;
        this.router.push({ path: "/leaderboards", query: { type: site } });
      }
    },
    getLeaderboardImage(site) {
      return this.currentLeaderboard === site
        ? this.leaderboardSwitchImages[site].active
        : this.leaderboardSwitchImages[site].inactive;
    },
    updateTotalPrize() {
      const rewardsKey = `VUE_APP_${this.currentLeaderboard.toUpperCase()}_LEADERBOARD_${
        this.previousLeaderboard ? "PREV" : "CURRENT"
      }_PRIZES`;
      const prizeList = JSON.parse(process.env[rewardsKey] || "[]");
      const total = prizeList.reduce((sum, val) => sum + Number(val), 0);
      this.totalPrize = total;
      this.animateNumber("animatedPrize", total);
    },
    toggleDisclaimer() {
      this.showDisclaimer = !this.showDisclaimer;
    },
    animateValues() {
      this.animateNumber("animatedPrize", this.totalPrize);
    },
    animateNumber(variable, targetValue) {
      let startValue = parseFloat(this[variable].replace(/,/g, "")) || 0;
      if (startValue === targetValue) return;

      const step = Math.max(1, Math.abs(targetValue - startValue) / 20);
      const isDecreasing = startValue > targetValue;

      const interval = setInterval(() => {
        if (isDecreasing) {
          startValue -= step;
          if (startValue <= targetValue) {
            startValue = targetValue;
            clearInterval(interval);
          }
        } else {
          startValue += step;
          if (startValue >= targetValue) {
            startValue = targetValue;
            clearInterval(interval);
          }
        }

        this[variable] = Number(startValue).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }, 50);
    },
    resetLeaderboardState() {
      this.leaderboardData = {};
      this.leaderboardPrevData = {};
      this.top3 = [];
      this.topRest = [];
      this.startDate = null;
      this.endDate = null;
      this.notFormattedEndDate = null;
      this.previousLeaderboard = false;
      this.days = 0;
      this.hours = 0;
      this.minutes = 0;
      this.seconds = 0;
      this.timer = null;

      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },
  },
  mounted() {
    this.fetchLeaderboard();
  },
};
</script>

<style>
/* Smooth fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>
